import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Box,
  IconButton,
  Alert,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CommonButton from "../../../components/CommonButton/CommonButton";
import { AddCardDialogProps, CardType } from "../types";

const cardTypes: CardType[] = [
  {
    value: "keycard",
    label: "Key Card",
    prefix: "06A",
    suffix: "A"
  }
];

const AddCardDialog: React.FC<AddCardDialogProps> = ({
  open,
  onClose,
  onConfirm,
  loading,
  error,
  cardType,
  onCardTypeChange,
  qrNumber,
  onQrNumberChange,
  qrSuffix,
  onQrSuffixChange
}) => {
  const selectedCardType =
    cardTypes.find((type) => type.value === cardType) || cardTypes[0];

  const handleQrNumberChange = (value: string) => {
    // Only allow digits and limit to 9 characters
    const numericValue = value.replace(/\D/g, "").slice(0, 9);
    onQrNumberChange(numericValue);
  };

  const handleQrSuffixChange = (value: string) => {
    // Only allow letters and limit to 4 characters, convert to uppercase
    const alphaValue = value
      .replace(/[^a-zA-Z]/g, "")
      .slice(0, 4)
      .toUpperCase();
    onQrSuffixChange(alphaValue);
  };

  return (
    <Dialog
      open={open}
      onClose={() => !loading && onClose()}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Typography sx={{ fontWeight: 600, fontSize: "18px" }}>
            Add New Card
          </Typography>
          <IconButton onClick={() => !loading && onClose()} disabled={loading}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        {error && (
          <div style={{ marginTop: "5px", marginBottom: "16px" }}>
            <Alert severity="error">{error}</Alert>
          </div>
        )}
        <Box sx={{ mt: 2, display: "flex", flexDirection: "column", gap: 2 }}>
          <FormControl fullWidth>
            <InputLabel>Type</InputLabel>
            <Select
              value={cardType}
              label="Type"
              onChange={(e) => onCardTypeChange(e.target.value)}
              disabled={loading}
            >
              {cardTypes.map((type) => (
                <MenuItem key={type.value} value={type.value}>
                  {type.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Box>
            <Typography
              variant="body2"
              sx={{ mb: 2, fontWeight: 600, color: "text.primary" }}
            >
              QR Code
            </Typography>
            <Box
              sx={{
                p: 2,
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 1,
                backgroundColor: "background.paper",
                boxShadow: "0 1px 3px rgba(0,0,0,0.1)"
              }}
            >
              <Grid
                container
                spacing={1}
                alignItems="center"
                justifyContent="center"
              >
                <Grid item>
                  <TextField
                    value={selectedCardType.prefix}
                    disabled
                    size="small"
                    sx={{
                      width: "70px",
                      "& .MuiOutlinedInput-root": {
                        backgroundColor: "grey.100",
                        "& fieldset": {
                          borderColor: "grey.300"
                        }
                      }
                    }}
                    inputProps={{
                      style: {
                        textAlign: "center",
                        fontWeight: 600,
                        color: "text.secondary"
                      }
                    }}
                  />
                </Grid>
                <Grid item>
                  <Typography
                    variant="body2"
                    sx={{
                      mx: 1,
                      color: "text.secondary",
                      fontWeight: 500
                    }}
                  >
                    -
                  </Typography>
                </Grid>
                <Grid item>
                  <TextField
                    label="Number"
                    value={qrNumber}
                    onChange={(e) => handleQrNumberChange(e.target.value)}
                    size="small"
                    disabled={loading}
                    placeholder="9 digits"
                    sx={{
                      width: "140px",
                      "& .MuiOutlinedInput-root": {
                        "&:hover fieldset": {
                          borderColor: "primary.main"
                        }
                      }
                    }}
                    inputProps={{
                      maxLength: 9,
                      style: {
                        textAlign: "center",
                        fontWeight: 500
                      }
                    }}
                  />
                </Grid>
                <Grid item>
                  <Typography
                    variant="body2"
                    sx={{
                      mx: 1,
                      color: "text.secondary",
                      fontWeight: 500
                    }}
                  >
                    -
                  </Typography>
                </Grid>
                <Grid item>
                  <TextField
                    value={selectedCardType.suffix}
                    disabled
                    size="small"
                    sx={{
                      width: "50px",
                      "& .MuiOutlinedInput-root": {
                        backgroundColor: "grey.100",
                        "& fieldset": {
                          borderColor: "grey.300"
                        }
                      }
                    }}
                    inputProps={{
                      style: {
                        textAlign: "center",
                        fontWeight: 600,
                        color: "text.secondary"
                      }
                    }}
                  />
                </Grid>
                <Grid item>
                  <Typography
                    variant="body2"
                    sx={{
                      mx: 1,
                      color: "text.secondary",
                      fontWeight: 500
                    }}
                  >
                    -
                  </Typography>
                </Grid>
                <Grid item>
                  <TextField
                    label="Suffix"
                    value={qrSuffix}
                    onChange={(e) => handleQrSuffixChange(e.target.value)}
                    size="small"
                    disabled={loading}
                    placeholder="4 letters"
                    sx={{
                      width: "90px",
                      "& .MuiOutlinedInput-root": {
                        "&:hover fieldset": {
                          borderColor: "primary.main"
                        }
                      }
                    }}
                    inputProps={{
                      maxLength: 4,
                      style: {
                        textAlign: "center",
                        fontWeight: 500
                      }
                    }}
                  />
                </Grid>
              </Grid>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                  mt: 2,
                  display: "block",
                  textAlign: "center",
                  fontStyle: "italic"
                }}
              >
                Format: <strong>{selectedCardType.prefix}</strong> -{" "}
                <strong>9 digits</strong> -{" "}
                <strong>{selectedCardType.suffix}</strong> -{" "}
                <strong>4 letters</strong>
              </Typography>
            </Box>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <CommonButton onClick={() => !loading && onClose()} disabled={loading}>
          Cancel
        </CommonButton>
        <CommonButton
          onClick={onConfirm}
          disabled={
            loading ||
            !qrNumber ||
            qrNumber.length !== 9 ||
            !qrSuffix ||
            qrSuffix.length !== 4
          }
          isLoading={loading}
        >
          {loading ? "Adding..." : "Add"}
        </CommonButton>
      </DialogActions>
    </Dialog>
  );
};

export default AddCardDialog;
